import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, Star, Building2, CalendarClock, MessageSquare, ShieldCheck, Crown, AlertTriangle, Lock, Unlock, Wand2, Loader2, Mail, UserX, Sparkles } from "lucide-react";
import { displayPrice } from "@/lib/price";

// -------------------------------------------------------------------------
// Small presentational helpers
// -------------------------------------------------------------------------
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function StarBar({ n }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={12}
          className={i <= n ? "fill-[#C9A85F] text-[#C9A85F]" : "text-muted-foreground"}
          strokeWidth={1.5}
        />
      ))}
    </span>
  );
}

// -------------------------------------------------------------------------
// Main page
// -------------------------------------------------------------------------
export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("reviews");
  const [businesses, setBusinesses] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bizQuery, setBizQuery] = useState("");
  const [bizPlatFilter, setBizPlatFilter] = useState("all"); // "all" | "platinum" | "regular"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, o, r] = await Promise.all([
        api.get("/admin/businesses"),
        api.get("/admin/openings"),
        api.get("/admin/reviews"),
      ]);
      setBusinesses(b.data);
      setOpenings(o.data);
      setReviews(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(
    () => ({
      businesses: businesses.length,
      openings: openings.length,
      reviews: reviews.filter((r) => !r.hidden).length,
      hidden: reviews.filter((r) => r.hidden).length,
    }),
    [businesses, openings, reviews],
  );

  const softDelete = async (id) => {
    // Optimistic — flip hidden immediately, roll back on failure.
    setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, hidden: true } : r)));
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review hidden");
    } catch (e) {
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, hidden: false } : r)));
      toast.error(formatApiError(e));
    }
  };

  const restore = async (id) => {
    setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, hidden: false } : r)));
    try {
      await api.post(`/admin/reviews/${id}/restore`);
      toast.success("Review restored");
    } catch (e) {
      setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, hidden: true } : r)));
      toast.error(formatApiError(e));
    }
  };

  // Flip the Platinum tier on a single business. Optimistic — we roll back
  // if the PATCH fails so the UI never lies about server state.
  const togglePlatinum = async (id, next) => {
    setBusinesses((bs) =>
      bs.map((b) => (b.id === id ? { ...b, is_platinum: next } : b)),
    );
    try {
      await api.patch(`/admin/businesses/${id}/platinum`, { is_platinum: next });
      toast.success(next ? "Marked as Platinum" : "Removed from Platinum");
    } catch (e) {
      setBusinesses((bs) =>
        bs.map((b) => (b.id === id ? { ...b, is_platinum: !next } : b)),
      );
      toast.error(formatApiError(e));
    }
  };

  const platinumCount = useMemo(
    () => businesses.filter((b) => b.is_platinum).length,
    [businesses],
  );

  const filteredBusinesses = useMemo(() => {
    const q = bizQuery.trim().toLowerCase();
    return businesses.filter((b) => {
      if (bizPlatFilter === "platinum" && !b.is_platinum) return false;
      if (bizPlatFilter === "regular" && b.is_platinum) return false;
      if (!q) return true;
      const hay = [
        b.business_name,
        b.name,
        b.email,
        b.address,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [businesses, bizQuery, bizPlatFilter]);

  return (
    <div className="mx-auto max-w-[1400px] p-4 md:p-8">
      {/* Header */}
      <div className="border border-border">
        <div className="p-6 md:p-8 border-b border-border flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F] flex items-center gap-2">
              <ShieldCheck size={12} /> admin · {user?.email}
            </p>
            <h1 className="mt-2 text-4xl leading-tight tracking-tight md:text-5xl">
              Moderation.
            </h1>
            <p className="mt-3 max-w-lg text-sm text-muted-foreground">
              Full read on every business, opening, and review. Only your email
              (<span className="mono">{user?.email}</span>) sees this page — everyone else gets a 403.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-border">
          {[
            { label: "Businesses", value: stats.businesses },
            { label: "Platinum", value: platinumCount },
            { label: "Openings", value: stats.openings },
            { label: "Reviews", value: stats.reviews },
          ].map((s, i) => (
            <div
              key={s.label}
              data-testid={`admin-stat-${s.label.toLowerCase()}`}
              className={`p-4 md:p-6 ${i < 3 ? "border-r border-border" : ""}`}
            >
              <p className="mono text-3xl font-medium md:text-4xl">
                {s.value.toString().padStart(2, "0")}
              </p>
              <p className="mono mt-1 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex border border-border w-fit">
        {[
          { id: "reviews", label: "Reviews", Icon: MessageSquare },
          { id: "businesses", label: "Businesses", Icon: Building2 },
          { id: "openings", label: "Openings", Icon: CalendarClock },
          { id: "analytics", label: "Analytics", Icon: Sparkles },
          { id: "waitlist", label: "Waitlist", Icon: Crown },
          { id: "danger", label: "Danger Zone", Icon: AlertTriangle },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-widest transition-colors ${
              tab === t.id ? "bg-foreground text-background" : "hover:bg-muted"
            }`}
          >
            <t.Icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="mono mt-6 p-8 border border-border text-xs text-muted-foreground">
          Loading…
        </div>
      ) : (
        <div className="mt-6 border border-border">
          {tab === "reviews" && (
            <ReviewsTable reviews={reviews} onDelete={softDelete} onRestore={restore} />
          )}
          {tab === "businesses" && (
            <BusinessesTable
              businesses={filteredBusinesses}
              totalCount={businesses.length}
              onTogglePlatinum={togglePlatinum}
              query={bizQuery}
              setQuery={setBizQuery}
              platFilter={bizPlatFilter}
              setPlatFilter={setBizPlatFilter}
            />
          )}
          {tab === "openings" && <OpeningsTable openings={openings} />}
          {tab === "analytics" && <AnalyticsPanel />}
          {tab === "waitlist" && <WaitlistPanel />}
          {tab === "danger" && <DangerZone onDataChanged={load} />}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Sub-tables
// -------------------------------------------------------------------------
function ReviewsTable({ reviews, onDelete, onRestore }) {
  if (reviews.length === 0) {
    return (
      <div className="p-8 mono text-xs text-muted-foreground">
        No reviews yet.
      </div>
    );
  }
  return (
    <ul data-testid="admin-reviews-list">
      {reviews.map((r) => (
        <li
          key={r.id}
          data-testid={`admin-review-row-${r.id}`}
          className={`grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1fr_auto] md:items-start md:gap-6 md:px-6 ${
            r.hidden ? "bg-muted/40" : ""
          }`}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <StarBar n={r.rating} />
              <span className="text-sm font-medium">{r.consumer_name}</span>
              <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                → {r.business_name || "Unknown business"}
              </span>
              {r.hidden && (
                <span
                  data-testid={`review-hidden-badge-${r.id}`}
                  className="mono px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-[#F87171]/10 text-[#F87171] border border-[#F87171]/40"
                >
                  Hidden
                </span>
              )}
            </div>
            <p className={`mt-2 text-sm ${r.hidden ? "line-through text-muted-foreground" : ""}`}>
              {r.text}
            </p>
            <p className="mono mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
              {fmtDate(r.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2 md:justify-end">
            {r.hidden ? (
              <button
                data-testid={`review-restore-btn-${r.id}`}
                onClick={() => onRestore(r.id)}
                className="mono border border-border px-3 py-1.5 text-[11px] uppercase tracking-widest hover:border-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                Restore
              </button>
            ) : (
              <button
                data-testid={`review-delete-btn-${r.id}`}
                onClick={() => {
                  if (window.confirm("Hide this review from the mobile app?")) {
                    onDelete(r.id);
                  }
                }}
                className="inline-flex items-center gap-1.5 bg-[#F87171] text-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest hover:bg-[#d63329] active:scale-[0.98] transition-transform"
              >
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function BusinessesTable({
  businesses,
  totalCount,
  onTogglePlatinum,
  query,
  setQuery,
  platFilter,
  setPlatFilter,
}) {
  const filterChips = [
    { id: "all", label: "All" },
    { id: "platinum", label: "Platinum only" },
    { id: "regular", label: "Non-platinum" },
  ];
  return (
    <div>
      <div className="border-b border-border p-4 md:px-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <input
          type="search"
          data-testid="admin-biz-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email or suburb…"
          className="mono h-10 w-full md:w-80 rounded-none border border-border bg-background px-3 text-xs focus:outline-none focus:border-[#C9A85F]"
        />
        <div className="flex items-center gap-2 flex-wrap">
          {filterChips.map((c) => (
            <button
              key={c.id}
              data-testid={`admin-biz-filter-${c.id}`}
              onClick={() => setPlatFilter(c.id)}
              aria-pressed={platFilter === c.id}
              className={`mono inline-flex items-center gap-1 border px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${
                platFilter === c.id
                  ? "border-[#C9A85F] bg-[#C9A85F]/15 text-[#8A7333]"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
            >
              {c.id === "platinum" && <Crown size={10} className="text-[#C9A85F]" />}
              {c.label}
            </button>
          ))}
          <span
            data-testid="admin-biz-count"
            className="mono ml-1 text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            {businesses.length} of {totalCount}
          </span>
        </div>
      </div>
      {businesses.length === 0 ? (
        <div className="p-8 mono text-xs text-muted-foreground">
          {totalCount === 0
            ? "No businesses registered yet."
            : "No businesses match this filter."}
        </div>
      ) : (
        <ul data-testid="admin-businesses-list">
          {businesses.map((b) => {
            const isP = !!b.is_platinum;
            return (
              <li
                key={b.id}
                data-testid={`admin-business-row-${b.id}`}
                className={`grid grid-cols-1 gap-3 border-b border-border p-4 md:grid-cols-[1fr_1fr_auto_auto] md:items-center md:gap-6 md:px-6 ${
                  isP ? "bg-[#C9A85F]/5" : ""
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium truncate">{b.business_name || b.name}</p>
                    {isP && (
                      <span
                        data-testid={`business-platinum-badge-${b.id}`}
                        className="mono inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest border border-[#C9A85F] text-[#8A7333] bg-[#C9A85F]/15"
                      >
                        <Crown size={10} className="fill-[#C9A85F] text-[#C9A85F]" /> Platinum
                      </span>
                    )}
                  </div>
                  <p className="mono text-[11px] text-muted-foreground truncate">{b.email}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground truncate">
                    {b.address || "— no address yet —"}
                  </p>
                </div>
                <div className="flex items-center gap-3 md:justify-end mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span className="bg-muted px-2 py-1">
                    {b.opening_count ?? 0} openings
                  </span>
                  <span>{fmtDate(b.created_at)}</span>
                </div>
                <div className="md:justify-self-end">
                  <button
                    data-testid={`platinum-toggle-${b.id}`}
                    onClick={() => onTogglePlatinum(b.id, !isP)}
                    aria-pressed={isP}
                    className={`mono inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest border transition-colors ${
                      isP
                        ? "bg-gradient-to-r from-[#C9A85F] to-[#E6D3A3] text-[#2A2724] border-[#C9A85F] hover:opacity-90"
                        : "border-border hover:border-[#C9A85F] hover:text-[#8A7333]"
                    }`}
                  >
                    <Crown size={12} className={isP ? "fill-[#2A2724]" : ""} />
                    {isP ? "Platinum" : "Make Platinum"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function OpeningsTable({ openings }) {
  if (openings.length === 0) {
    return (
      <div className="p-8 mono text-xs text-muted-foreground">
        No openings yet.
      </div>
    );
  }
  const statusClass = (s) =>
    s === "open"
      ? "bg-[#C9A85F] text-black"
      : s === "filled"
        ? "bg-[#34D399]/15 text-[#059669]"
        : "bg-muted text-muted-foreground";
  return (
    <ul data-testid="admin-openings-list">
      {openings.map((o) => (
        <li
          key={o.id}
          data-testid={`admin-opening-row-${o.id}`}
          className="grid grid-cols-1 gap-2 border-b border-border p-4 md:grid-cols-[1fr_1fr_auto] md:items-center md:gap-6 md:px-6"
        >
          <div className="min-w-0">
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#C9A85F]">
              {o.service_type}
            </p>
            <p className="mt-0.5 text-sm truncate">{o.title}</p>
            <p className="mono text-[11px] text-muted-foreground truncate">
              {o.business_name}
            </p>
          </div>
          <div className="min-w-0 mono text-[11px] text-muted-foreground">
            <p>{fmtDate(o.start_time)}</p>
            <p className="truncate">{o.address}</p>
          </div>
          <div className="flex items-center gap-2 md:justify-end">
            {o.price != null && o.price !== "" && (
              <span className="mono text-xs">{displayPrice(o.price)}</span>
            )}
            <span
              className={`mono px-2 py-1 text-[10px] uppercase tracking-widest ${statusClass(o.status)}`}
            >
              {o.status}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}


// -------------------------------------------------------------------------
// Danger Zone — one-shot admin operations that mutate the DB in bulk.
// Every destructive action requires the operator to type an explicit
// confirmation string, matching the server-side `confirm` field on
// `/api/admin/purge-test-data`. Two-step "type-then-click" pattern prevents
// accidental clicks from nuking production.
// -------------------------------------------------------------------------
function WaitlistPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/foundation/waitlist").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!data) return <div className="mono p-6 text-xs text-muted-foreground">Loading…</div>;
  return (
    <div className="p-4 md:p-6" data-testid="admin-waitlist-panel">
      <h2 className="text-xl tracking-tight">Foundation Waitlist</h2>
      <p className="mono mt-0.5 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
        {data.total_waiting} waiting · seats offered are held for 48h
      </p>
      {data.cities.length === 0 && (
        <p data-testid="admin-waitlist-empty" className="mt-6 text-sm text-muted-foreground">Nobody on the waitlist yet.</p>
      )}
      {data.cities.map((c) => (
        <div key={c.city} data-testid={`admin-waitlist-city-${c.city.toLowerCase()}`} className="mt-6 border border-border">
          <div className="flex flex-wrap justify-between gap-2 border-b border-border p-3">
            <p className="font-medium">{c.city}</p>
            <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {c.subscribed} subscribed · {c.held} held · {c.seats_total} seats
            </p>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {c.entries.map((e, i) => (
                <tr key={e.id} className="border-b border-border last:border-0">
                  <td className="mono w-10 p-3 text-xs text-muted-foreground">{i + 1}</td>
                  <td className="p-3">{e.email}{e.business_name ? ` · ${e.business_name}` : ""}</td>
                  <td className="mono p-3 text-[10px] uppercase tracking-widest">
                    {e.status === "offered" ? `held until ${fmtDate(e.offer_expires_at)}` : "waiting"}
                  </td>
                  <td className="mono p-3 text-[10px] text-muted-foreground">{fmtDate(e.created_at)} · {e.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/analytics/cover-cropper-funnel?days=${days}`
      );
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || {};
  const rates = data?.rates || {};
  const opened = totals.opened || 0;
  const cancelled = totals.cancelled || 0;
  const confirmed = totals.confirmed || 0;
  const uploadedOk = totals.upload_succeeded || 0;
  const uploadedFail = totals.upload_failed || 0;

  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : null);
  const barWidth = opened > 0 ? (n) => `${Math.round((n / opened) * 100)}%` : () => "0%";

  return (
    <div className="p-4 md:p-6" data-testid="admin-analytics-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl tracking-tight">Cover Photo Cropper Funnel</h2>
          <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-0.5">
            Where businesses drop off between picking a file and finishing the upload
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Window
          </label>
          <select
            data-testid="analytics-window-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="mono text-[10px] uppercase tracking-widest border border-border bg-background px-2 py-1.5"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            type="button"
            data-testid="analytics-refresh"
            onClick={load}
            disabled={loading}
            className="mono inline-flex items-center gap-1.5 border border-border px-2.5 py-1.5 text-[10px] uppercase tracking-widest hover:border-foreground transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            Refresh
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="mono mt-6 p-8 border border-border text-xs text-muted-foreground">
          Loading…
        </div>
      ) : (
        <>
          {/* Funnel bars — each stage as a percentage of `opened` */}
          <div className="mt-6 space-y-3" data-testid="analytics-funnel-bars">
            {[
              { key: "opened", label: "Opened", n: opened, note: "File picked, modal mounted" },
              { key: "confirmed", label: "Confirmed", n: confirmed, note: "Clicked “Use this crop”" },
              { key: "upload_succeeded", label: "Upload OK", n: uploadedOk, note: "File_id returned" },
              { key: "cancelled", label: "Cancelled", n: cancelled, note: "Dismissed the modal", muted: true },
              { key: "upload_failed", label: "Upload failed", n: uploadedFail, note: "Network / server error", muted: true },
            ].map((row) => (
              <div key={row.key} data-testid={`funnel-row-${row.key}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <div>
                    <span className="mono text-[10px] uppercase tracking-widest">
                      {row.label}
                    </span>{" "}
                    <span className="mono text-[10px] text-muted-foreground">— {row.note}</span>
                  </div>
                  <div className="mono text-xs">
                    <span className="font-semibold">{row.n.toLocaleString()}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      {pct(row.n, opened) !== null ? `(${pct(row.n, opened)}%)` : ""}
                    </span>
                  </div>
                </div>
                <div className="mt-1 h-2 w-full border border-border bg-muted/40">
                  <div
                    className={`h-full ${row.muted ? "bg-muted-foreground/40" : "bg-[#C9A85F]"}`}
                    style={{ width: barWidth(row.n) }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Headline rates */}
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <div className="border border-border p-4" data-testid="rate-confirm">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Confirm rate
              </div>
              <div className="mt-1 text-3xl font-light">
                {rates.confirm_rate != null
                  ? `${Math.round(rates.confirm_rate * 100)}%`
                  : "—"}
              </div>
              <div className="mono text-[10px] text-muted-foreground">
                of opens finish the crop
              </div>
            </div>
            <div className="border border-border p-4" data-testid="rate-upload">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Upload success rate
              </div>
              <div className="mt-1 text-3xl font-light">
                {rates.upload_success_rate != null
                  ? `${Math.round(rates.upload_success_rate * 100)}%`
                  : "—"}
              </div>
              <div className="mono text-[10px] text-muted-foreground">
                of confirms finish the upload
              </div>
            </div>
          </div>

          <p className="mt-6 mono text-[10px] text-muted-foreground">
            Range: {data?.from || "—"} → {data?.to || "—"}. Daily rows:{" "}
            {data?.daily?.length || 0}. Events bucket in UTC.
          </p>
        </>
      )}
    </div>
  );
}



function DangerZone({ onDataChanged }) {
  const [confirmText, setConfirmText] = useState("");
  const [lockReseed, setLockReseed] = useState(true);
  const [wipeAllUsers, setWipeAllUsers] = useState(false);
  const [purging, setPurging] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [locking, setLocking] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewTo, setPreviewTo] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [reconcileEmail, setReconcileEmail] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const purge = async () => {
    if (confirmText !== "DELETE_ALL_BOOKINGS") {
      toast.error("Type DELETE_ALL_BOOKINGS to confirm.");
      return;
    }
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-test-data", {
        confirm: confirmText,
        lock_reseed: lockReseed,
        wipe_all_users: wipeAllUsers,
      });
      toast.success(data.message || "Purge complete.");
      setLastResult(data);
      setConfirmText("");
      onDataChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPurging(false);
    }
  };

  const migrate = async () => {
    setMigrating(true);
    try {
      const { data } = await api.post("/admin/migrate-categories");
      toast.success(
        `Merged legacy categories → ${data.users_updated} user(s) + ${data.openings_updated} opening(s) updated.`
      );
      setLastResult(data);
      onDataChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMigrating(false);
    }
  };

  const toggleLock = async (locked) => {
    setLocking(true);
    try {
      const { data } = await api.post("/admin/reseed-lock", { locked });
      toast.success(`Demo seed lock ${data.seed_lock}.`);
      setLastResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLocking(false);
    }
  };

  const sendPreview = async () => {
    setPreviewing(true);
    try {
      const body = previewTo.trim() ? { to: previewTo.trim() } : {};
      const { data } = await api.post("/admin/send-digest-preview", body);
      toast.success(
        data.synthetic
          ? `Synthetic preview sent to ${data.sent_to} (no real requests yesterday).`
          : `Preview sent to ${data.sent_to} using ${data.sample_business}'s stats.`
      );
      setLastResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPreviewing(false);
    }
  };

  const resetTestAccount = async () => {
    const email = resetEmail.trim().toLowerCase();
    const confirm = resetConfirm.trim().toLowerCase();
    if (!email) {
      toast.error("Enter the email of the account to wipe.");
      return;
    }
    if (email !== confirm) {
      toast.error("Confirmation email doesn't match.");
      return;
    }
    setResetting(true);
    try {
      const { data } = await api.delete("/admin/users/by-email", {
        data: { email, confirm_email: confirm },
      });
      toast.success(data.message || `Account ${email} wiped.`);
      setLastResult(data);
      setResetEmail("");
      setResetConfirm("");
      onDataChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  const reconcileByEmail = async () => {
    const email = reconcileEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Enter the email of the account to reconcile.");
      return;
    }
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/payments/reconcile-by-email", {
        email,
      });
      if (data.activated) {
        toast.success(
          `Activated ${email} on ${data.plan_tier || "their plan"} — status ${data.subscription_status}.`
        );
      } else {
        toast.warning(
          `No activation for ${email}: ${data.reason || "unknown reason"}.`
        );
      }
      setLastResult(data);
      setReconcileEmail("");
      onDataChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-danger-zone">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={16} className="text-[#B8422A]" />
        <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#B8422A]">
          Danger Zone
        </p>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl">
        One-shot admin operations. Everything here is irreversible. Read the
        button copy carefully before clicking.
      </p>

      {/* Card 1 — Purge Test Data */}
      <div
        data-testid="danger-card-purge"
        className="mt-6 border-2 border-[#B8422A] bg-[#B8422A]/5 p-6 md:p-8"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Trash2 size={16} className="text-[#B8422A]" />
          Purge Test Data
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Deletes <strong>every opening</strong> and <strong>every booking request</strong> in
          the DB, plus all <code>@demo.com</code> demo user accounts. Your admin
          login, real signups, subscriptions, and consumer accounts are preserved
          (unless you tick "wipe all users").
        </p>

        <div className="mt-6 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="danger-purge-lock-reseed"
              checked={lockReseed}
              onChange={(e) => setLockReseed(e.target.checked)}
              className="mt-1 accent-[#C9A85F]"
            />
            <div>
              <p className="text-sm font-medium">Lock demo reseed after purge</p>
              <p className="text-xs text-muted-foreground">
                Blocks the demo seeder from re-running on the next boot — even
                if <code>SEED_DEMO_DATA=true</code> is still set on the env.
                Recommended.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="danger-purge-wipe-users"
              checked={wipeAllUsers}
              onChange={(e) => setWipeAllUsers(e.target.checked)}
              className="mt-1 accent-[#B8422A]"
            />
            <div>
              <p className="text-sm font-medium text-[#B8422A]">
                Nuclear: also delete every non-admin user
              </p>
              <p className="text-xs text-muted-foreground">
                Deletes real business signups AND consumer accounts too. Only
                your admin login survives. Very rarely what you want.
              </p>
            </div>
          </label>

          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Type <code className="text-foreground">DELETE_ALL_BOOKINGS</code> to confirm
            </label>
            <input
              type="text"
              data-testid="danger-purge-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE_ALL_BOOKINGS"
              className="mt-2 w-full max-w-md border border-border bg-background px-3 py-2 text-sm mono focus:outline-none focus:border-[#B8422A]"
            />
          </div>

          <button
            type="button"
            data-testid="danger-purge-submit-btn"
            onClick={purge}
            disabled={purging || confirmText !== "DELETE_ALL_BOOKINGS"}
            className={`inline-flex items-center gap-2 px-6 h-11 text-xs font-semibold uppercase tracking-widest transition-all ${
              confirmText === "DELETE_ALL_BOOKINGS" && !purging
                ? "bg-[#B8422A] text-white hover:bg-[#a03920] active:scale-[0.98]"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }`}
          >
            {purging ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {purging ? "Purging…" : wipeAllUsers ? "Nuke Everything" : "Purge Test Data"}
          </button>
        </div>
      </div>

      {/* Card 2 — Demo Reseed Lock Toggle */}
      <div
        data-testid="danger-card-reseed"
        className="mt-6 border border-border p-6 md:p-8"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Lock size={16} className="text-[#C9A85F]" />
          Demo Reseed Lock
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Standalone toggle for the runtime seed lock. Engage it to keep prod
          bare after a purge without redeploying. Release it later to reseed
          demo pins on next boot (needs <code>SEED_DEMO_DATA=true</code> on env).
        </p>
        <div className="mt-4 flex gap-3">
          <button
            type="button"
            data-testid="danger-lock-engage-btn"
            onClick={() => toggleLock(true)}
            disabled={locking}
            className="inline-flex items-center gap-2 px-5 h-11 text-xs font-semibold uppercase tracking-widest border border-foreground hover:bg-foreground hover:text-background transition-colors"
          >
            {locking ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            Engage lock
          </button>
          <button
            type="button"
            data-testid="danger-lock-release-btn"
            onClick={() => toggleLock(false)}
            disabled={locking}
            className="inline-flex items-center gap-2 px-5 h-11 text-xs font-semibold uppercase tracking-widest border border-border hover:border-foreground transition-colors"
          >
            {locking ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />}
            Release lock
          </button>
        </div>
      </div>

      {/* Card 3 — Category Migration */}
      <div
        data-testid="danger-card-migrate"
        className="mt-6 border border-border p-6 md:p-8"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Wand2 size={16} className="text-[#C9A85F]" />
          Migrate Legacy Categories
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Folds legacy <code>Skin</code> and <code>Injectables</code> category
          values into the merged <code>Skin and Injectables</code> across every
          user profile and opening. Idempotent — safe to run multiple times.
        </p>
        <button
          type="button"
          data-testid="danger-migrate-btn"
          onClick={migrate}
          disabled={migrating}
          className="mt-4 inline-flex items-center gap-2 px-5 h-11 text-xs font-semibold uppercase tracking-widest gold-metallic-bg text-black hover:opacity-90 active:scale-[0.98] transition-transform"
        >
          {migrating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {migrating ? "Migrating…" : "Run migration"}
        </button>
      </div>

      {/* Card 4 — Digest Preview */}
      <div
        data-testid="danger-card-digest-preview"
        className="mt-6 border border-border p-6 md:p-8"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Mail size={16} className="text-[#C9A85F]" />
          Preview Daily Digest
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Fire a one-off preview of the 8am Sydney digest email so you can
          check the formatting right now. Uses yesterday's top-request
          business, or synthetic sample data if nothing happened yesterday.
          Marked <code>[PREVIEW]</code> in the subject. Does <strong>not</strong> block
          tomorrow's real 8am send.
        </p>
        <div className="mt-4">
          <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Send to (leave blank to send to admin)
          </label>
          <input
            type="email"
            data-testid="danger-preview-to-input"
            value={previewTo}
            onChange={(e) => setPreviewTo(e.target.value)}
            placeholder="hello@bookaway.com.au"
            className="mt-2 w-full max-w-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-[#C9A85F]"
          />
        </div>
        <button
          type="button"
          data-testid="danger-preview-send-btn"
          onClick={sendPreview}
          disabled={previewing}
          className="mt-4 inline-flex items-center gap-2 px-5 h-11 text-xs font-semibold uppercase tracking-widest border border-foreground hover:bg-foreground hover:text-background transition-colors disabled:opacity-50"
        >
          {previewing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {previewing ? "Sending…" : "Send preview digest now"}
        </button>
      </div>

      {/* Card 5 — Reset Test Account by Email */}
      <div
        data-testid="danger-card-reset-user"
        className="mt-6 border-2 border-[#B8422A] bg-[#B8422A]/5 p-6 md:p-8"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <UserX size={16} className="text-[#B8422A]" />
          Reset Test Account
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Wipe a single user account by email so the same address is free
          to re-register. Deletes every artefact tied to them: openings,
          requests, reviews, scans, followers, follows, devices, sessions,
          password reset tokens, payment transaction records, API keys.
          Your admin account is protected — the endpoint refuses to
          self-delete. <strong>Does not cancel Stripe subscriptions</strong>
          — for real subs, use the Stripe dashboard or customer portal
          first.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Email of account to delete
            </label>
            <input
              type="email"
              data-testid="danger-reset-email-input"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="tester@example.com"
              autoComplete="off"
              className="mt-2 w-full max-w-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-[#B8422A]"
            />
          </div>

          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Re-type email to confirm
            </label>
            <input
              type="email"
              data-testid="danger-reset-confirm-input"
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="tester@example.com"
              autoComplete="off"
              className="mt-2 w-full max-w-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-[#B8422A]"
            />
          </div>

          <button
            type="button"
            data-testid="danger-reset-submit-btn"
            onClick={resetTestAccount}
            disabled={
              resetting ||
              !resetEmail.trim() ||
              resetEmail.trim().toLowerCase() !== resetConfirm.trim().toLowerCase()
            }
            className={`inline-flex items-center gap-2 px-6 h-11 text-xs font-semibold uppercase tracking-widest transition-all ${
              resetEmail.trim() &&
              resetEmail.trim().toLowerCase() === resetConfirm.trim().toLowerCase() &&
              !resetting
                ? "bg-[#B8422A] text-white hover:bg-[#a03920] active:scale-[0.98]"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }`}
          >
            {resetting ? <Loader2 size={14} className="animate-spin" /> : <UserX size={14} />}
            {resetting ? "Wiping…" : "Wipe account"}
          </button>
        </div>
      </div>

      {/* Card 6 — Reconcile Stripe Subscription by Email */}
      <div
        data-testid="danger-card-reconcile"
        className="mt-6 border border-[#C9A85F] p-6 md:p-8"
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#C9A85F]" />
          Force-Activate Stripe Subscription
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Support fallback for when a customer paid in Stripe but the
          webhook crashed mid-flight and their dashboard is still gated by
          the paywall. Looks up their most recent active/trialing/past_due
          Stripe subscription directly and activates them — bypasses the
          <code> payment_transactions</code> row entirely. Safe to re-run
          (idempotent). Returns <code>activated:false</code> gracefully if
          they have no Stripe customer or no active sub.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Email of stuck account
            </label>
            <input
              type="email"
              data-testid="danger-reconcile-email-input"
              value={reconcileEmail}
              onChange={(e) => setReconcileEmail(e.target.value)}
              placeholder="daniella.toscano@hotmail.com"
              autoComplete="off"
              className="mt-2 w-full max-w-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-[#C9A85F]"
            />
          </div>
          <button
            type="button"
            data-testid="danger-reconcile-submit-btn"
            onClick={reconcileByEmail}
            disabled={reconciling || !reconcileEmail.trim()}
            className={`inline-flex items-center gap-2 px-6 h-11 text-xs font-semibold uppercase tracking-widest transition-all ${
              reconcileEmail.trim() && !reconciling
                ? "gold-metallic-bg text-black hover:opacity-90 active:scale-[0.98]"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }`}
          >
            {reconciling ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {reconciling ? "Reconciling…" : "Activate from Stripe"}
          </button>
        </div>
      </div>

      {/* Last operation result */}
      {lastResult && (
        <div
          data-testid="danger-last-result"
          className="mt-6 border border-border bg-muted/30 p-4 mono text-xs"
        >
          <p className="uppercase tracking-widest text-muted-foreground text-[10px] mb-2">
            Last operation result
          </p>
          <pre className="whitespace-pre-wrap break-all">
            {JSON.stringify(lastResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
